import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Ticket, Calendar, Filter, Search, Trophy, Clock, CheckCircle2 } from 'lucide-react';
import {
  TicketCard,
  Card,
  Badge,
  Input,
  Button,
  ResponsiveDialog,
  Tabs,
  EmptyState,
  useViewport,
  cn,
  formatNaira,
  formatDate,
  formatTime,
  staggerChildren,
  fadeInUp,
} from '@raffle/ui';
import { useAuth } from '../providers/AuthProvider';
import { useTicketsStore } from '../stores/ticketsStore';

type TicketStatus = 'all' | 'active' | 'won' | 'lost';

export const MyTicketsPage: React.FC = () => {
  const TicketIcon = ({ className }: { className?: string }) => (
    <Ticket className={className} />
  );
  const { isDesktop, isMobile } = useViewport();
  const { user } = useAuth();

  const { tickets, isLoading, error, fetchTickets } = useTicketsStore();

  useEffect(() => {
    if (!user) return;
    void fetchTickets({ page: 1 });
  }, [fetchTickets, user]);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<TicketStatus>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);

  const userTickets = useMemo(() => tickets, [tickets]);

  // Apply filters
  const filteredTickets = useMemo(() => {
    return userTickets.filter((ticket) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesProperty = ticket.property?.title.toLowerCase().includes(query);
        const matchesNumber = ticket.ticketNumber.toString().toLowerCase().includes(query);
        if (!matchesProperty && !matchesNumber) return false;
      }

      // Status filter
      if (statusFilter !== 'all') {
        if (statusFilter === 'active' && ticket.status !== 'active') return false;
        if (statusFilter === 'won' && ticket.status !== 'won') return false;
        if (statusFilter === 'lost' && ticket.status !== 'lost') return false;
      }

      return true;
    });
  }, [userTickets, searchQuery, statusFilter]);

  // Group tickets by property for "ticket stack" view
  const ticketsByProperty = useMemo(() => {
    const grouped = new Map<string, typeof filteredTickets>();
    filteredTickets.forEach((ticket) => {
      const existing = grouped.get(ticket.propertyId) || [];
      grouped.set(ticket.propertyId, [...existing, ticket]);
    });
    return Array.from(grouped.entries());
  }, [filteredTickets]);

  // Stats
  const stats = useMemo(() => {
    const total = userTickets.length;
    const active = userTickets.filter((t) => t.status === 'active').length;
    const won = userTickets.filter((t) => t.status === 'won').length;
    const totalSpent = userTickets.reduce((sum, t) => sum + (t.property?.ticketPrice ?? 0), 0);
    return { total, active, won, totalSpent };
  }, [userTickets]);

  const selectedTicket = selectedTicketId
    ? filteredTickets.find((t) => t.id === selectedTicketId)
    : null;

  const statusTabs = [
    { value: 'all' as const, label: 'All', count: userTickets.length },
    { value: 'active' as const, label: 'Active', count: stats.active },
    { value: 'won' as const, label: 'Won', count: stats.won },
    { value: 'lost' as const, label: 'Lost', count: userTickets.length - stats.active - stats.won },
  ];

  return (
    <div className="page-container">
      <section className="content-wrapper py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-text-primary mb-2">
              My Tickets
            </h1>
            <p className="text-text-secondary">
              Manage and track all your raffle entries
            </p>
          </div>
        </div>

        {/* Stats Cards (Desktop) */}
        {isDesktop && (
          <motion.div
            className="grid grid-cols-4 gap-4 mb-8"
            variants={staggerChildren}
            initial="hidden"
            animate="show"
          >
            {[
              { label: 'Total Tickets', value: stats.total, icon: Ticket, color: 'text-gold-500' },
              { label: 'Active Entries', value: stats.active, icon: Clock, color: 'text-blue-400' },
              { label: 'Wins', value: stats.won, icon: Trophy, color: 'text-green-400' },
              { label: 'Total Spent', value: formatNaira(stats.totalSpent), icon: CheckCircle2, color: 'text-purple-400' },
            ].map((stat, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-text-muted mb-1">{stat.label}</p>
                      <p className="font-mono text-2xl font-bold text-text-primary tabular-nums">
                        {stat.value}
                      </p>
                    </div>
                    <stat.icon className={cn('w-5 h-5', stat.color)} />
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <Input
              type="search"
              placeholder="Search by property or ticket number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12"
            />
          </div>
          
          {isMobile && (
            <Button variant="outline" onClick={() => setShowFilters(true)}>
              <Filter className="w-4 h-4 mr-2" />
              Filter
            </Button>
          )}
        </div>

        {/* Status Tabs */}
        <Tabs
          value={statusFilter}
          onChange={(value) => setStatusFilter(value as TicketStatus)}
          tabs={statusTabs.map((tab) => ({
            value: tab.value,
            label: tab.label,
            badge: tab.count,
          }))}
          className="mb-6"
        />

        {/* Tickets */}
        {error ? (
          <EmptyState
            icon={TicketIcon}
            title="Unable to load tickets"
            description={error}
            action={{
              label: 'Retry',
              onClick: () => {
                if (user) void fetchTickets({ page: 1 });
              },
            }}
          />
        ) : isLoading ? (
          <EmptyState
            icon={TicketIcon}
            title="Loading tickets"
            description="Fetching your raffle entries…"
          />
        ) : filteredTickets.length === 0 ? (
          <EmptyState
            icon={TicketIcon}
            title="No tickets found"
            description={
              searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : "You haven't purchased any raffle tickets yet"
            }
            action={
              searchQuery || statusFilter !== 'all'
                ? {
                    label: 'Clear Filters',
                    onClick: () => {
                      setSearchQuery('');
                      setStatusFilter('all');
                    },
                  }
                : {
                    label: 'Browse Properties',
                    href: '/properties',
                  }
            }
          />
        ) : (
          <motion.div
            className="space-y-6"
            variants={staggerChildren}
            initial="hidden"
            animate="show"
          >
            {ticketsByProperty.map(([propertyId, tickets]) => {
              const property = tickets[0]?.property;
              if (!property) return null;

              return (
                <motion.div key={propertyId} variants={fadeInUp}>
                  <Card className="overflow-hidden">
                    {/* Property Header */}
                    <div className="flex gap-4 p-4 border-b border-white/5">
                      <img
                        src={property.images?.[0]?.url || ''}
                        alt=""
                        className="w-20 h-14 rounded-lg object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-display font-semibold text-text-primary truncate">
                          {property.title}
                        </h3>
                        <div className="flex items-center gap-4 mt-1">
                          <Badge
                            variant={
                              property.status === 'active'
                                ? 'success'
                                : property.status === 'drawn'
                                ? 'gold'
                                : 'default'
                            }
                          >
                            {property.status.charAt(0).toUpperCase() + property.status.slice(1)}
                          </Badge>
                          <span className="text-xs text-text-muted flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Draw: {formatDate(property.drawDate)}
                          </span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm text-text-muted">Your Tickets</p>
                        <p className="font-mono text-lg font-bold text-gold-500 tabular-nums">
                          {tickets.length}
                        </p>
                      </div>
                    </div>

                    {/* Ticket List */}
                    <div className={cn(
                      'grid gap-3 p-4',
                      isDesktop ? 'grid-cols-3' : 'grid-cols-1'
                    )}>
                      {tickets.map((ticket) => (
                        <TicketCard
                          key={ticket.id}
                          ticket={{
                            id: ticket.id,
                            ticketNumber: ticket.ticketNumber,
                            propertyId: ticket.propertyId,
                            propertyTitle: property.title,
                            propertyImage: property.images?.[0]?.url,
                            purchaseDate: ticket.createdAt,
                            price: property.ticketPrice,
                            status: ticket.status === 'refunded' ? 'lost' : ticket.status,
                          }}
                          onClick={() => setSelectedTicketId(ticket.id)}
                        />
                      ))}
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </section>

      {/* Ticket Detail Modal */}
      <ResponsiveDialog
        isOpen={!!selectedTicket}
        onClose={() => setSelectedTicketId(null)}
        title="Ticket Details"
      >
        {selectedTicket && (
          <div className="p-6 space-y-6">
            {/* Property Info */}
            <div className="flex gap-4 p-4 rounded-vault bg-surface-2/50">
              <img
                src={selectedTicket.property?.images?.[0]?.url || ''}
                alt=""
                className="w-24 h-16 rounded-lg object-cover"
              />
              <div className="flex-1 min-w-0">
                <h4 className="font-display font-semibold text-text-primary truncate">
                  {selectedTicket.property?.title}
                </h4>
              </div>
            </div>

            {/* Ticket Info */}
            <div className="space-y-4">
              <div className="flex justify-between py-3 border-b border-white/5">
                <span className="text-text-muted">Ticket Number</span>
                <span className="font-mono font-semibold text-gold-500">
                  {selectedTicket.ticketNumber}
                </span>
              </div>
              <div className="flex justify-between py-3 border-b border-white/5">
                <span className="text-text-muted">Purchase Date</span>
                <span className="text-text-primary">
                  {formatDate(selectedTicket.createdAt)} at {formatTime(selectedTicket.createdAt)}
                </span>
              </div>
              <div className="flex justify-between py-3 border-b border-white/5">
                <span className="text-text-muted">Purchase Price</span>
                <span className="font-mono text-text-primary">
                  {formatNaira(selectedTicket.property?.ticketPrice ?? 0)}
                </span>
              </div>
              <div className="flex justify-between py-3 border-b border-white/5">
                <span className="text-text-muted">Status</span>
                <Badge
                  variant={
                    selectedTicket.status === 'active'
                      ? 'success'
                      : selectedTicket.status === 'won'
                      ? 'gold'
                      : 'default'
                  }
                >
                  {selectedTicket.status.charAt(0).toUpperCase() + selectedTicket.status.slice(1)}
                </Badge>
              </div>
              {selectedTicket.property?.status === 'active' && (
                <div className="flex justify-between py-3">
                  <span className="text-text-muted">Draw Date</span>
                  <span className="text-text-primary">
                    {formatDate(selectedTicket.property.drawDate)}
                  </span>
                </div>
              )}
            </div>

            {/* Actions */}
            {selectedTicket.status === 'won' && (
              <div className="p-4 rounded-vault bg-gold-500/10 border border-gold-500/20 text-center">
                <Trophy className="w-8 h-8 text-gold-500 mx-auto mb-2" />
                <p className="font-display font-semibold text-gold-500">
                  Congratulations! You Won!
                </p>
                <p className="text-sm text-text-muted mt-1">
                  Our team will contact you with next steps
                </p>
              </div>
            )}

            <Button variant="outline" fullWidth onClick={() => setSelectedTicketId(null)}>
              Close
            </Button>
          </div>
        )}
      </ResponsiveDialog>

      {/* Mobile Filter Dialog */}
      <ResponsiveDialog
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        title="Filter Tickets"
      >
        <div className="p-6 space-y-6">
          <div className="space-y-3">
            <p className="text-sm font-medium text-text-primary">Status</p>
            {statusTabs.map((tab) => (
              <button
                key={tab.value}
                onClick={() => {
                  setStatusFilter(tab.value);
                  setShowFilters(false);
                }}
                className={cn(
                  'w-full flex items-center justify-between p-3 rounded-vault border transition-colors',
                  statusFilter === tab.value
                    ? 'bg-gold-500/10 border-gold-500/30 text-gold-500'
                    : 'bg-surface-2/50 border-white/5 text-text-secondary hover:border-white/10'
                )}
              >
                <span>{tab.label}</span>
                <span className="font-mono text-sm tabular-nums">{tab.count}</span>
              </button>
            ))}
          </div>
        </div>
      </ResponsiveDialog>
    </div>
  );
};
